import React, { ChangeEvent, FormEvent, useState } from 'react';

import { useSnackbar } from 'notistack';

import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';

import { resendEmail } from '~/services/SailthruService';

interface Props {
  open: boolean;
  handleClose: () => void;
  notificationId?: string;
  notificationIds?: Array<string>;
  notificationType?: string;
  initialEmail?: string;
}

function EmailFormModal({ open, handleClose, notificationId, notificationIds, notificationType, initialEmail }: Props) {
  const [email, setEmail] = useState(initialEmail ?? '');
  const [isValid, setIsValid] = useState(true);
  const [isDomainValid, setIsDomainValid] = useState(true);
  const { enqueueSnackbar } = useSnackbar();
  const ids = notificationIds ?? (notificationId ? [notificationId] : []);
  const isBulkForward = ids.length > 1;

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    const emailValue = e.target.value;
    setEmail(emailValue);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const domainRegex = /^[^\s@]+@luxuryescapes\.com$/;
    setIsValid(emailRegex.test(emailValue));
    setIsDomainValid(domainRegex.test(emailValue));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (isValid && isDomainValid) {
      try {
        for (const id of ids) {
          await resendEmail({ id, email: email });
        }
        enqueueSnackbar(isBulkForward ? 'Emails sent, please check inbox' : 'Email sent, please check inbox', {
          variant: 'success',
        });
        handleClose();
      } catch {
        enqueueSnackbar('Failed to resend email', { variant: 'error' });
      }
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Forward Email: {notificationType}</DialogTitle>
      <DialogContent>
        {isBulkForward && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This will forward {ids.length} emails to the entered address.
          </Typography>
        )}
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 2 }}>
          <TextField
            margin="dense"
            label="Forward To:"
            type="email"
            fullWidth
            variant="outlined"
            value={email}
            onChange={handleEmailChange}
            error={!isValid || !isDomainValid}
            helperText={
              !isValid
                ? 'Please enter a valid email address.'
                : !isDomainValid
                  ? 'Please enter an email address within the luxuryescapes.com domain.'
                  : ''
            }
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary">
          Cancel
        </Button>
        <Button type="submit" onClick={handleSubmit} color="primary" disabled={!isValid || !isDomainValid}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EmailFormModal;
